import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box, List, ListItem, ListItemText, Paper, makeStyles } from "@material-ui/core";
import TableInformation from "./TableInformation";
import TableImportWorker from "./TableImportWorker";
import ComplianceMatrixTab from "./ComplianceMatrixTab";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppDialog from "components/Dialog/AppDialog";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import { deleteWorker, getWorkerList, resendOnboarding, fetchWorkerOptionsList, getWorkerActiveTab, setWorkerActiveTab } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import Select from "react-select";
import { showToast } from "store/slices/toast";
import AppConfig from "config/AppConfig";

const useStyles = makeStyles(() => ({
    listTitle: {
        fontSize: '16px',
        color: AppColor.black,
        fontWeight: '500',
        padding: 0,
        margin: 0
    },
    listItem: {
        marginBottom: '20px',
        padding: '10px',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        borderRadius: '5px',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        '&:hover': {
            backgroundColor: AppColor.mainColor,
            color: AppColor.white
        }
    },
    container: {
        padding: "1rem",
        // backgroundColor: "#f9f7f6",
        minHeight: "calc(100vh - 80px)"
    },
    title: {
        fontSize: 22,
        fontWeight: 700,
        color: "#0D9488",
        marginBottom: 16,
    },
    card: {
        background: "#fff",
        borderRadius: '8px',
        border: "1px solid #E5E7EB",
        padding: 0,
        overflow: "hidden",
    },
    tabsContainer: {
        display: "flex",
        borderBottom: "1px solid #E5E7EB",
        flexWrap: "wrap",
    },
    tab: {
        padding: "9px 16px",
        fontSize: 12,
        color: "#6B7280",
        cursor: "pointer",
        borderBottom: "2.5px solid transparent",
        marginBottom: -1,
        whiteSpace: "nowrap",
        "&:hover": {
            color: "#111827",
        },
    },
    tabActive: {
        color: "#0D9488",
        borderBottomColor: AppColor.mainColor,
        fontWeight: 600,
    },
    tabContent: {
        padding: 16,
    },
}));

const WorkerTable = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const authInfo = useSelector(getAuthInfo);
    const workerList = useSelector(getWorkerList);
    
    const [selected, setSelected] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const savedActiveTab = useSelector(getWorkerActiveTab);
    const [activeTab, setActiveTabState] = useState(savedActiveTab);

    const setActiveTab = (index) => {
        setActiveTabState(index);
        dispatch(setWorkerActiveTab(index));
    };

    const [emailOptions, setEmailOptions] = useState([]);
    const [emailPage, setEmailPage] = useState(1);
    const [hasMoreEmail, setHasMoreEmail] = useState(true);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const loadedPageRef = useRef(0);

    const tableInfoRef = useRef();

    const loadMoreEmailOptions = async (isFirst = false) => {
        const pageToLoad = isFirst ? 1 : emailPage;

        if (loadingEmail || (!hasMoreEmail && !isFirst)) return;
        if (!isFirst && pageToLoad <= loadedPageRef.current) return;

        setLoadingEmail(true);
        try {
            const res = await dispatch(fetchWorkerOptionsList({ page: pageToLoad, size: 20, companyId: authInfo.companyId }));

            if (res.payload) {
                const newContents = res.payload.content || [];
                const newOptions = newContents
                  .filter(w => (w?.approvalStatus === AppConfig.APPROVAL_STATUS.PENDING || w?.approvalStatus === AppConfig.APPROVAL_STATUS.WAITING_FOR_APPROVAL))
                  .map(w => ({ value: w.id, label: `${w.name} (${w.email})`, status: w?.approvalStatus }));

                if (isFirst) {
                    setEmailOptions(newOptions);
                    loadedPageRef.current = 1;
                } else {
                    setEmailOptions(prev => [...prev, ...newOptions]);
                    loadedPageRef.current = pageToLoad;
                }
                setEmailPage(pageToLoad + 1);
                setHasMoreEmail(pageToLoad < res.payload.totalPages);
            }
        } finally {
            setLoadingEmail(false);
        }
    };

    useEffect(() => {
        if (showSendModal && emailOptions.length === 0) {
            loadMoreEmailOptions(true);
        }
    }, [showSendModal]);

    const _handleDeleteClients = async () => {
        setIsDeleting(true);
        try {
            await dispatch(deleteWorker({
                companyId: authInfo.companyId,
                ids: selected
            }));

            setShowDeleteModal(false);
            if (tableInfoRef.current) {
              const currentQueries = tableInfoRef.current?.refreshData();
              const dataLength = tableInfoRef.current?.getDataLength();
              if (
                dataLength === 1 ||
                (selected.length === dataLength && currentQueries.page > 1)
              ) {
                tableInfoRef.current.loadWithQueries({
                  ...currentQueries,
                  page: currentQueries.page - 1,
                });
              } else {
                tableInfoRef.current.refreshData();
              }
            }
            setSelected([]);
        } finally {
            setIsDeleting(false);
        }
    }

    const _handleGoToSettingAccounting = () => {
        history.push("/admin/setting-accounting");
        setShowConfirmModal(false);
    }

    const handleCreateWorker = () => {
        if (!authInfo.accountingType) {
            setShowConfirmModal(true);
        } else {
            history.push(`/admin/workers/create`);
        }
    };

    const handleSendOnboardingLink = async () => {
        setIsSending(true);
        try {
            for (const item of selectedEmails) {
                await dispatch(resendOnboarding({ workerId: item.value, domain: window.location.hostname }));
            }
            dispatch(showToast({ type: "success", message: t("text.send_success") }));
            setShowSendModal(false);
            setSelectedEmails([]);
        } finally {
            setIsSending(false);
        }
    };

    const tabs = [
        {
            label: t('label.workers'),
            component: <TableInformation ref={tableInfoRef} selected={selected} setSelected={setSelected} />
        },
        {
            label: t('customUI.complianceMatrix'),
            component: <ComplianceMatrixTab />
        },
        {
            label: t('label.importStaffs'),
            component: <TableImportWorker />
        }
    ];

    return (
        <Box className={classes.container}>
            <Box mb={2} display="flex" alignItems="center">
                <AppButton
                    onClick={() => handleCreateWorker()}
                    style={{ marginRight: '1rem' }}
                >
                  <i className="fas fa-plus mr-2" />
                  {t('button.create')}
                </AppButton>
                <AppButton
                    label={t('button.delete')}
                    buttonColor={AppColor.pink}
                    onClick={() => setShowDeleteModal(true)}
                    disabled={selected.length === 0 || isDeleting}
                    isLoading={isDeleting}
                    style={{ marginRight: '1rem' }}
                />
                <AppButton
                    onClick={() => setShowSendModal(true)}
                    mode="outlined"
                    style={{ backgroundColor: "#fff", borderWidth: "1px" }}
                    disabled={isSending}
                >
                  <i className="fas fa-paper-plane mr-2" />
                  {t('customUI.sendOnboardingLink')}
                </AppButton>
            </Box>

            <Paper className={classes.card}>
                <Box className={classes.tabsContainer}>
                    {tabs.map((tab, index) => (
                        <Box
                            key={index}
                            className={`${classes.tab} ${activeTab === index ? classes.tabActive : ""}`}
                            onClick={() => setActiveTab(index)}
                        >
                            {tab.label}
                        </Box>
                    ))}
                </Box>
                <Box className={classes.tabContent}>
                    {tabs[activeTab].component}
                </Box>
            </Paper>

            <AppConfirmDialog
                open={showDeleteModal}
                onConfirm={() => _handleDeleteClients()}
                onClose={() => setShowDeleteModal(false)}
                title={t('title.delete_workers')}
                content={t('text.delete_workers')}
                isDisabledButton={isDeleting}
            />
            <AppConfirmDialog
                open={showConfirmModal}
                onConfirm={() => _handleGoToSettingAccounting()}
                onClose={() => setShowConfirmModal(false)}
                title={t('title.check_accounting_type')}
                content={t('text.can_not_have_accounting')}
            />
            <AppDialog
                open={showSendModal}
                title={t('customUI.sendOnboardingLink')}
                onClose={() => setShowSendModal(false)}
                isDisabledButton={isSending}
                actions={[
                    {
                        label: t('button.cancel'),
                        onPress: () => setShowSendModal(false),
                        disabled: isSending,
                        mode: "outlined"
                    },
                    {
                        label: t('button.send'),
                        onPress: handleSendOnboardingLink,
                        disabled: selectedEmails.length === 0 || isSending,
                        isLoading: isSending
                    }
                ]}
            >
                <Box mb={3} style={{ minHeight: 200 }}>
                    <Select
                        isMulti
                        closeMenuOnSelect={false}
                        options={emailOptions}
                        value={selectedEmails}
                        onChange={setSelectedEmails}
                        placeholder={t('label.email')}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        onMenuScrollToBottom={() => loadMoreEmailOptions(false)}
                        isLoading={loadingEmail}
                    />
                </Box>
            </AppDialog>
        </Box>
    );
};

export default WorkerTable;